import React, { useMemo, useState } from 'react';
import { GitBranch, Home } from 'lucide-react';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { isWhiteLabel } from '../config/tenantConfig';
import {
  timelineFeatures,
  statusConfig,
  categoryColors,
  FeatureStatus,
} from '../data/featureTimeline';

const filterOptions: { label: string; value: FeatureStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Completed', value: 'completed' },
  { label: 'In Progress', value: 'in-progress' },
  { label: 'Planned', value: 'planned' },
  { label: 'Backlog', value: 'backlog' },
];

const parseDateForSort = (date: string): number => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return new Date(date).getTime();
  const monthMatch = date.match(/^(\w+)\s+(\d{4})$/);
  if (monthMatch) return new Date(`${monthMatch[1]} 1, ${monthMatch[2]}`).getTime();
  const qMatch = date.match(/^Q(\d)\s+(\d{4})$/);
  if (qMatch) {
    const month = (parseInt(qMatch[1]) - 1) * 3;
    return new Date(parseInt(qMatch[2]), month, 1).getTime();
  }
  return 0;
};

const formatDate = (date: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return date || '—';
};

interface FeatureTimelinePageProps {
  /** When true (inside OthersLayout), skip the outer StandardLayout + PageHeader
   *  so the parent layout owns the page chrome. */
  embedded?: boolean;
}

const FeatureTimelinePage: React.FC<FeatureTimelinePageProps> = ({ embedded = false }) => {
  const [filter, setFilter] = useState<FeatureStatus | 'all'>('all');

  const { sortedFeatures, counts } = useMemo(() => {
    const filtered = filter === 'all'
      ? timelineFeatures
      : timelineFeatures.filter((f) => f.status === filter);
    const sorted = [...filtered].sort((a, b) => parseDateForSort(b.date) - parseDateForSort(a.date));
    return {
      sortedFeatures: sorted,
      counts: {
        total: timelineFeatures.length,
        completed: timelineFeatures.filter((f) => f.status === 'completed').length,
        inProgress: timelineFeatures.filter((f) => f.status === 'in-progress').length,
        planned: timelineFeatures.filter((f) => f.status === 'planned').length,
        backlog: timelineFeatures.filter((f) => f.status === 'backlog').length,
      },
    };
  }, [filter]);

  const content = (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white tabular-nums">{counts.total}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed</div>
          <div className="text-xl font-bold mt-1 text-green-600 dark:text-green-400 tabular-nums">{counts.completed}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">In Progress</div>
          <div className="text-xl font-bold mt-1 text-blue-600 dark:text-blue-400 tabular-nums">{counts.inProgress}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Planned</div>
          <div className="text-xl font-bold mt-1 text-gray-600 dark:text-gray-400 tabular-nums">{counts.planned}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Backlog</div>
          <div className="text-xl font-bold mt-1 text-gray-500 dark:text-gray-500 tabular-nums">{counts.backlog}</div>
        </div>
      </div>

      {/* Main table card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Features ({sortedFeatures.length})</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Filter bar */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-2">
              {filterOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFilter(opt.value)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filter === opt.value
                      ? (isWhiteLabel() ? 'bg-zebu-600 text-white' : 'bg-blue-600 text-white')
                      : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Table / cards — table at md+, cards on mobile so the long
              description text doesn't force horizontal scroll. */}
          {sortedFeatures.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              No features match the selected filter.
            </div>
          ) : (
            <>
              {/* Mobile (<md): card list */}
              <ul className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {sortedFeatures.map((feature) => {
                  const cat = categoryColors[feature.category];
                  const status = statusConfig[feature.status];
                  return (
                    <li key={feature.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{feature.title}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{feature.description}</div>
                        </div>
                        <span className={`shrink-0 inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${cat.bg} ${cat.text}`}>
                          {feature.category}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap text-xs">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full ${status.dotColor} ${feature.status === 'in-progress' ? 'animate-pulse' : ''}`} />
                          <span className={`font-medium ${status.textColor}`}>{status.label}</span>
                          <span className="text-gray-400 dark:text-gray-500">·</span>
                          <span className="text-gray-700 dark:text-gray-300">{formatDate(feature.date)}</span>
                        </div>
                        {feature.requester && (
                          <span className="text-gray-500 dark:text-gray-400">
                            {feature.requester}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Desktop (md+): table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Feature</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Requested By</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedFeatures.map((feature) => {
                      const cat = categoryColors[feature.category];
                      const status = statusConfig[feature.status];
                      return (
                        <tr key={feature.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{feature.title}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-md">{feature.description}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider ${cat.bg} ${cat.text}`}>
                              {feature.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className={`inline-block w-2 h-2 rounded-full ${status.dotColor} ${feature.status === 'in-progress' ? 'animate-pulse' : ''}`} />
                              <span className={`text-xs font-medium ${status.textColor}`}>{status.label}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            {formatDate(feature.date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            {feature.requester || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );

  if (embedded) return content;

  return (
    <StandardLayout>
      <PageHeader
        title="Feature Timeline"
        pageType="management"
        icon={<GitBranch className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Others' },
        ]}
      />
      {content}
    </StandardLayout>
  );
};

export default FeatureTimelinePage;